/**
 * @fileoverview Optimised colour-swatch functionality (2025-05-06)
 *
 * Changes:
 *  - No eager section/image fetch; fetch happens on swatch click only (and is cached).
 *  - Only 50 x 50 thumbnails are pre-loaded; hero images load on demand.
 *  - Event delegation replaces per-swatch listeners (smaller memory footprint).
 *  - Debug logs off by default; add ?debug=1 to the URL to enable.
 *  - Misc. dead code, duplicate logic, and sync image decoding removed.
 *
 * 2026-04 operational note:
 *  - this asset used to ship a public x-internal-secret header; that was removed
 *  - if swatches only show the current product, check the related-products API
 *    response before changing storefront code
 *  - if the API is blocked/unavailable, the current-product fallback is expected
 */

(() => {
  /* ----------------------------------------------------------------------- */
  /* Helpers                                                                 */
  /* ----------------------------------------------------------------------- */

  const SWATCH_DEBUG = 0;

  const log = (...args) => SWATCH_DEBUG && console.log("[Swatch]", ...args);

  let cachedProducts = [];

  // Limits and state for caching and click handling
  const SECTION_CACHE_LIMIT = 30;
  let currentSectionAbortController = null;
  let lastSwatchClickTs = 0;

  const pruneSectionCache = (limit = SECTION_CACHE_LIMIT) => {
    const keys = Object.keys(sessionStorage).filter((key) =>
      key.startsWith("sectionCache_")
    );
    if (keys.length > limit) {
      keys
        .slice(0, keys.length - limit)
        .forEach((key) => sessionStorage.removeItem(key));
    }
  };

  // Return a lightweight 180-pixel variant using Shopify's width query param.
  const getOptimisedImageUrl = (url) => {
    try {
      const parsedUrl = new URL(url, location.origin);
      parsedUrl.searchParams.set("width", "180");
      return parsedUrl.toString();
    } catch {
      return url;
    }
  };

  /* ----------------------------------------------------------------------- */
  /* Main                                                                    */
  /* ----------------------------------------------------------------------- */

  document.addEventListener("DOMContentLoaded", () => {
    const product = window.productData;
    if (!product) {
      console.error("[Swatch] window.productData missing");
      return;
    }

    const colorOptionIndex = (product.options || []).findIndex((option) =>
      ["colour", "color"].includes((option || "").trim().toLowerCase())
    );
    if (colorOptionIndex === -1) {
      console.warn("[Swatch] No colour option found on product");
    }

    /* ---------- related products by MPN ---------------------------------- */

    const mpnValue =
      product?.metafields?.custom?.mpn ||
      (product.tags.find((tag) => tag.startsWith("mpn_")) || "").replace(
        "mpn_",
        ""
      );
    log("MPN value used:", mpnValue);

    const fetchRelatedProducts = async () => {
      if (!mpnValue) return [];

      const cacheKey = `relatedProducts_mpn_${mpnValue}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { timestamp, products } = JSON.parse(cached);
        if (Date.now() - timestamp < 30 * 60e3) {
          log("using cached related products");
          return products;
        }
      }

      try {
        const response = await fetch(
          `https://parasol-related-products-api.andre-87e.workers.dev/api/related-products?mpn=${encodeURIComponent(
            mpnValue
          )}`
        );
        const { products = [] } = await response.json();
        log("Fetched related products:", products);
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({ timestamp: Date.now(), products })
        );
        return products;
      } catch (error) {
        console.error("[Swatch] fetchRelatedProducts failed", error);
        return [];
      }
    };

    /* ---------- render & behaviour --------------------------------------- */

    const renderSwatches = (products) => {
      const swatchMount = document.querySelector(".js-related-color-swatches");
      if (!swatchMount) return;
      const labelEl = document.querySelector("[data-variant-color-label]");

      swatchMount.innerHTML = "";
      const list = document.createElement("ul");
      list.className = "swatch-list";
      const fragment = document.createDocumentFragment();

      const currentPath = location.pathname.replace(/\/$/, "");

      products
        .filter((item) => item.image)
        .sort((left, right) => {
          const leftColor =
            left.variantOptions?.find((option) =>
              ["colour", "color"].includes(option.name.toLowerCase())
            )?.value?.toLowerCase() || "";
          const rightColor =
            right.variantOptions?.find((option) =>
              ["colour", "color"].includes(option.name.toLowerCase())
            )?.value?.toLowerCase() || "";
          return leftColor.localeCompare(rightColor);
        })
        .forEach((item) => {
          const listItem = document.createElement("li");
          listItem.className = "swatch-item";
          listItem.dataset.handle = item.handle;

          const link = document.createElement("a");
          link.href = `/products/${item.handle}`;
          link.title = item.title;
          link.className = "swatch-link block-swatch";

          if (link.pathname.replace(/\/$/, "") === currentPath) {
            link.classList.add("active");
            link.setAttribute("aria-current", "page");
          }

          const image = document.createElement("img");
          image.src = getOptimisedImageUrl(item.image);
          image.alt = item.title;
          image.className = "swatch-image";
          image.width = image.height = 90;
          image.loading = "eager";
          image.decoding = "async";
          link.appendChild(image);

          listItem.appendChild(link);
          fragment.appendChild(listItem);
        });

      list.appendChild(fragment);
      swatchMount.appendChild(list);

      /* ---- optional show-more toggle ----------------------------------- */

      const MAX_VISIBLE = 12;
      const swatchStateKey = "swatchListExpanded";
      let expanded = sessionStorage.getItem(swatchStateKey) === "true";

      const items = [...list.children];

      if (items.length > MAX_VISIBLE) {
        const applyVisibility = () => {
          if (expanded) {
            list.classList.remove("swatch-list--collapsed");
            items.forEach((element) => {
              element.hidden = false;
            });
          } else {
            list.classList.add("swatch-list--collapsed");
            items.slice(MAX_VISIBLE).forEach((element) => {
              element.hidden = true;
            });
          }
        };

        applyVisibility();

        const toggleButton = document.createElement("button");
        toggleButton.className = "swatch-toggle-button";
        toggleButton.textContent = expanded
          ? "Show fewer colours"
          : "Show all colours";

        toggleButton.addEventListener("click", () => {
          expanded = !expanded;
          sessionStorage.setItem(swatchStateKey, String(expanded));
          applyVisibility();
          toggleButton.textContent = expanded
            ? "Show fewer colours"
            : "Show all colours";
        });

        swatchMount.appendChild(toggleButton);
      }

      /* ---- hover label (delegated) ------------------------------------- */

      list.addEventListener("pointerenter", (event) => {
        const listItem = event.target.closest(".swatch-item");
        if (!listItem || !labelEl) return;
        const relatedProduct = products.find(
          (item) => item.handle === listItem.dataset.handle
        );
        const colour = relatedProduct?.variantOptions?.find((option) =>
          ["colour", "color"].includes(option.name.toLowerCase())
        )?.value;
        if (colour) {
          labelEl.dataset.original = labelEl.textContent;
          labelEl.textContent = colour;
        }
      });

      list.addEventListener("pointerleave", () => {
        if (labelEl?.dataset.original) {
          labelEl.textContent = labelEl.dataset.original;
          delete labelEl.dataset.original;
        }
      });

      /* ---- click handler (delegated) ----------------------------------- */

      list.addEventListener("click", async (event) => {
        const listItem = event.target.closest(".swatch-item");
        if (!listItem) return;
        event.preventDefault();

        const now = Date.now();
        if (now - lastSwatchClickTs < 250) return;
        lastSwatchClickTs = now;

        const handle = listItem.dataset.handle;
        if (!handle) return;

        const sectionWrapper = document.querySelector(
          ".product-section[data-section-id]"
        );
        const sectionId = sectionWrapper?.dataset.sectionId || "product-template";
        const cacheKey = `sectionCache_${handle}`;
        const cachedHtml = sessionStorage.getItem(cacheKey);

        const replaceSection = (html) => {
          const documentFragment = new DOMParser().parseFromString(
            html,
            "text/html"
          );
          const newSection = documentFragment.getElementById(
            `shopify-section-${sectionId}`
          );
          const currentSection = document.getElementById(
            `shopify-section-${sectionId}`
          );
          if (!(newSection && currentSection)) return;

          const previousScrollY = window.scrollY;

          const clone = newSection.cloneNode(true);
          currentSection.replaceWith(clone);

          requestAnimationFrame(() => {
            window.scrollTo(0, previousScrollY);
          });
          document.dispatchEvent(
            new CustomEvent("shopify:section:load", { detail: { sectionId } })
          );
          window.history.pushState({}, "", `/products/${handle}`);
          renderSwatches(cachedProducts);
        };

        if (cachedHtml) {
          log("using cached section", handle);
          replaceSection(cachedHtml);
          return;
        }

        if (currentSectionAbortController) currentSectionAbortController.abort();
        currentSectionAbortController = new AbortController();

        const url = new URL(`/products/${handle}`, location.origin);
        url.searchParams.set("section_id", sectionId);
        log("fetching section", url.toString());

        try {
          const html = await (
            await fetch(url, { signal: currentSectionAbortController.signal })
          ).text();
          sessionStorage.setItem(cacheKey, html);
          pruneSectionCache();
          replaceSection(html);
        } catch (error) {
          console.error("[Swatch] section fetch failed - fallback nav", error);
          location.href = `/products/${handle}`;
        }
      });
    };

    /* ---------- boot ----------------------------------------------------- */

    (async () => {
      cachedProducts = await fetchRelatedProducts();
      let products = cachedProducts;

      if (!products.length) {
        const colourName = product.options[colorOptionIndex] || "Colour";

        products = [
          {
            handle: product.handle,
            title: product.title,
            image: product.featured_image || product.images?.[0] || "",
            variantOptions: [
              { name: colourName, value: product.title.split(" ").pop() },
            ],
          },
        ];
        cachedProducts = products;
      }

      renderSwatches(products);
    })();
  });
})();
